import { useEffect, useState } from "react";
import { Tabs, Tab } from "@mui/material";
import { X } from "lucide-react";
import "../../../styles/components/management/Chat/AISettings.css";

export default function AISettings({ onClose }) {
  const [tab, setTab] = useState(0);
  const [companyRules, setCompanyRules] = useState("");
  const [properties, setProperties] = useState([]);
  const [selectedPropertyId, setSelectedPropertyId] = useState("");
  const [propertyRules, setPropertyRules] = useState("");
  const [units, setUnits] = useState([]);
  const [selectedUnitId, setSelectedUnitId] = useState("");
  const [unitRules, setUnitRules] = useState("");
  const [rooms, setRooms] = useState([]);
  const [selectedRoomId, setSelectedRoomId] = useState("");
  const [roomRules, setRoomRules] = useState("");

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_BASE}/app/API/rbmgt/chat/AI/getSettings.php`)
      .then((res) => res.json())
      .then((data) => {
        console.log("[Initial Fetch] Full Settings Object:", data);
        setCompanyRules(data.companyRules ?? "");
        setProperties(data.properties ?? []);
      })
      .catch((err) => console.error("Failed to fetch AI settings:", err));
  }, []);

  const handlePropertyChange = (e) => {
    const propertyId = e.target.value;
    console.log("[Property Change] Selected Property ID:", propertyId);
    console.log("[Property Change] All Properties:", properties);

    setSelectedPropertyId(propertyId);

    const property = properties.find(
      (p) => String(p.PROPERTY_ID) === String(propertyId)
    );
    console.log("[Property Change] Matched Property Object:", property);

    setPropertyRules(property?.propertyRules ?? "");
    setUnits(property?.units ?? []);
    setSelectedUnitId("");
    setUnitRules("");
    setRooms([]);
    setSelectedRoomId("");
    setRoomRules("");
  };

  const handleUnitChange = (e) => {
    const unitId = e.target.value;
    console.log("[Unit Change] Selected Unit ID:", unitId);
    console.log("[Unit Change] Units List:", units);

    setSelectedUnitId(unitId);

    const unit = units.find((u) => String(u.UNIT_ID) === String(unitId));
    console.log("[Unit Change] Matched Unit Object:", unit);

    setUnitRules(unit?.unitRules ?? "");
    setRooms(unit?.rooms ?? []);
    setSelectedRoomId("");
    setRoomRules("");
  };

  const handleRoomChange = (e) => {
    const roomId = e.target.value;
    console.log("[Room Change] Selected Room ID:", roomId);
    console.log("[Room Change] Rooms List:", rooms);

    setSelectedRoomId(roomId);

    const room = rooms.find((r) => String(r.ROOM_ID) === String(roomId));
    console.log("[Room Change] Matched Room Object:", room);

    setRoomRules(room?.roomRules ?? "");
  };

  const handleSave = () => {
    fetch(`${process.env.REACT_APP_API_BASE}/app/API/rbmgt/chat/AI/saveSettings.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        companyRules: companyRules || "",
        propertyId: selectedPropertyId || "",
        propertyRules: propertyRules || "",
        unitId: selectedUnitId || "",
        unitRules: unitRules || "",
        roomId: selectedRoomId || "",
        roomRules: roomRules || "",
      }),
    })
      .then((res) => res.json())
      .then(() => alert("Settings saved successfully."))
      .catch((err) => console.error("Error saving settings:", err));
  };

  return (
    <div className="ai-settings-overlay">
      <div className="ai-settings-container">
        <div className="ai-settings-header">
          <h2>AI Settings</h2>
          <button className="close-button" onClick={onClose}><X size={20} /></button>
        </div>

        <div className="ai-settings-tabs-wrapper">
          <Tabs
            value={tab}
            onChange={(_, newValue) => setTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Company" />
            <Tab label="Property" />
            <Tab label="Unit" disabled={!selectedPropertyId} />
            <Tab label="Room" disabled={!selectedUnitId} />
          </Tabs>
        </div>

        {tab === 0 && (
          <div className="field-group">
            <label>Company Rules:</label>
            <textarea value={companyRules} onChange={(e) => setCompanyRules(e.target.value)} />
          </div>
        )}

        {tab === 1 && (
          <>
            <div className="field-group">
              <label>Select Property:</label>
              <select value={selectedPropertyId} onChange={handlePropertyChange}>
                <option value="">-- Select Property --</option>
                {properties.map((p, index) => (
                  <option key={`property-${p.PROPERTY_ID || index}`} value={p.PROPERTY_ID}>
                    {p.PROPERTY_NAME}
                  </option>
                ))}
              </select>
            </div>

            {selectedPropertyId && (
              <div className="field-group">
                <label>Property Rules:</label>
                <textarea value={propertyRules} onChange={(e) => setPropertyRules(e.target.value)} />
              </div>
            )}
          </>
        )}

        {tab === 2 && selectedPropertyId && (
          <>
            <div className="field-group">
              <label>Select Unit:</label>
              <select value={selectedUnitId} onChange={handleUnitChange}>
                <option value="">-- Select Unit --</option>
                {units.map((u, index) => (
                  <option key={`unit-${u.UNIT_ID || index}`} value={u.UNIT_ID}>
                    {u.UNIT_NAME}
                  </option>
                ))}
              </select>
            </div>

            {selectedUnitId && (
              <div className="field-group">
                <label>Unit Rules:</label>
                <textarea value={unitRules} onChange={(e) => setUnitRules(e.target.value)} />
              </div>
            )}
          </>
        )}

        {tab === 3 && selectedUnitId && (
          <>
            <div className="field-group">
              <label>Select Room:</label>
              <select value={selectedRoomId} onChange={handleRoomChange}>
                <option value="">-- Select Room --</option>
                {rooms.map((r) => (
                  <option key={`room-${r.ROOM_ID}`} value={r.ROOM_ID}>
                    {r.ROOM_NAME}
                  </option>
                ))}
              </select>
            </div>

            {selectedRoomId && (
              <div className="field-group">
                <label>Room Rules:</label>
                <textarea value={roomRules} onChange={(e) => setRoomRules(e.target.value)} />
              </div>
            )}
          </>
        )}

        <button className="save-button" onClick={handleSave}>💾 Save Settings</button>
      </div>
    </div>
  );
}
